/**
 * Feedback Reviews — the SME's work queue, and every verdict on record.
 *
 * A review row is never created here: the server writes one PENDING row per
 * REVIEWER the moment feedback arrives. So there is no "New" button — only
 * completing a row you were given.
 *
 * Two views, because they answer different questions:
 *   My queue  — what I still have to do  (reviewStatus = PENDING, assigned to me)
 *   All       — what has been concluded, by anyone
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from 'sharedUi/Dialog';
import { Dropdown } from 'sharedUi/Dropdown';
import { Button } from 'sharedUi/Button';
import { Tag } from 'sharedUi/Tag';
import { TextArea } from 'sharedUi/TextArea';
import { InputSwitch } from 'sharedUi/InputSwitch';
import { Message } from 'sharedUi/Message';
import FormField from '../../../components/common/form-field';
import SaasDataTable, { type SaasColumn } from '../../../components/common/saas-data-table';
import { ListCount } from '../../../components/common/list-count';
import { feedbackReviewService } from '../../../services/feedback-submission.service';
import type { FeedbackReview } from '../../../types/feedback-submission.types';
import { useToastApi } from '../../../hooks/use-toast-api';
import { api } from '../../../services/api';
import { useReviewOutcomes } from '../use-feedback-event-value-sets';
import SubmissionDetail from '../submission-detail';
import { useHasPermission } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../constants/permissions';
import ProjectFilter from '../../../components/common/project-filter';
import ProjectCell from '../../../components/common/project-cell';
import { useAccessibleProjects } from '../../../hooks/useProjectScope';
import type { FeedbackScope } from '../feedback-scope';

/**
 * Which feedback this instance lists. The AI screen and the Feedback module are
 * the same screen over the same collection, pointed at different component types
 * — so the filter travels as a prop rather than the screen being copied.
 */
interface ScopedTabProps { scope?: FeedbackScope }

const OUTCOME_INFO =
  'What you concluded. Valid feedback = the user is right. Expected behavior = it '
  + 'works as designed and they expected something else. Needs investigation = you '
  + 'cannot tell yet.';

const ReviewsTab = ({ scope }: ScopedTabProps = {}) => {
  const qc = useQueryClient();
  const { toast, ok, fail } = useToastApi();
  // Recording/changing a verdict is a FEEDBACK_REVIEW write — hide it from watchers
  // and anyone without the permission (the server enforces it too).
  const canReview = useHasPermission(PERMISSIONS.FEEDBACK_REVIEW_UPDATE);

  const [mine, setMine] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [openSubmissionId, setOpenSubmissionId] = useState<string | null>(null);
  /* The review row the open feedback belongs to — the verdict is recorded against
     the ROW, not the submission, so opening the feedback has to carry it along. */
  const [openRow, setOpenRow] = useState<FeedbackReview | null>(null);
  // '' = every project the caller may read, merged.
  const [appFilter, setAppFilter] = useState('');

  // the verdict dialog
  const [target, setTarget] = useState<FeedbackReview | null>(null);
  const [outcome, setOutcome] = useState('');
  const [comments, setComments] = useState('');
  const [actionRequired, setActionRequired] = useState(false);
  const [actionNotes, setActionNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const { options: outcomeOptions, seeded: outcomesSeeded } = useReviewOutcomes();

  const { projects } = useAccessibleProjects();

  const listQ = useQuery({
    queryKey: ['feedback-reviews', { scope, mine, page, pageSize, appFilter }],
    queryFn: () => (mine
      ? feedbackReviewService.inbox({ page, page_size: pageSize, scope, appId: appFilter || undefined })
      : feedbackReviewService.list({ page, page_size: pageSize, scope, appId: appFilter || undefined })),
    staleTime: 30_000,
  });

  const usersQ = useQuery({
    queryKey: ['users-names-minimal'],
    queryFn: async () => {
      const res = await api.get<{ data: { _id: string; name: string }[] }>('/api/v1/users/names-minimal');
      return res.data.data || [];
    },
    staleTime: 10 * 60 * 1000,
  });
  const nameOf = useMemo(() => {
    const map = new Map((usersQ.data ?? []).map((u) => [u._id, u.name]));
    return (id?: string | null) => (id ? map.get(id) || id : '—');
  }, [usersQ.data]);

  /** Rows written before smeRole existed were all reviewers. */
  const isWatcher = (r: FeedbackReview) =>
    (r.smeRole || 'REVIEWER').toUpperCase() === 'WATCHER';

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['feedback-reviews'] });
    qc.invalidateQueries({ queryKey: ['feedback-submissions'] });
    qc.invalidateQueries({ queryKey: ['feedback-submission'] });
  };

  const saveM = useMutation({
    mutationFn: (r: FeedbackReview) => feedbackReviewService.recordVerdict(r._id, {
      reviewStatus: 'REVIEWED',
      reviewOutcome: outcome,
      reviewComments: comments.trim() || null,
      actionRequired,
      actionNotes: actionRequired ? actionNotes.trim() : null,
    }),
    onSuccess: () => { ok('Review saved'); closeDialog(); invalidate(); },
    onError: (e) => fail(e),
  });

  const openDialog = (r: FeedbackReview) => {
    setTarget(r);
    setOutcome(r.reviewOutcome || '');
    setComments(r.reviewComments || '');
    setActionRequired(!!r.actionRequired);
    setActionNotes(r.actionNotes || '');
    setSubmitted(false);
  };
  const closeDialog = () => { setTarget(null); setSubmitted(false); };

  /** The two conditional rules the server enforces, checked here for a better error. */
  const errors = useMemo(() => {
    const e: string[] = [];
    if (!outcome) e.push('Pick what you concluded.');
    if (actionRequired && !actionNotes.trim()) e.push('Say what needs doing.');
    return e;
  }, [outcome, actionRequired, actionNotes]);

  const columns: SaasColumn<FeedbackReview>[] = useMemo(() => [
    {
      /* First, and not hideable. A queue row whose subject is missing is
         unusable: every row renders as the same reviewer in the same state, so
         there is nothing to tell you WHICH item you are about to decide on. */
      field: 'submission', header: 'Feedback', hideable: false, style: { minWidth: 250 },
      body: (r) => (
        <div>
          <div className="font-mono text-xs font-semibold text-ink">
            {r.submission?.feedbackKey || '—'}
          </div>
          <div className="text-xs text-ink-muted">
            {r.submission?.component?.componentType
              || r.submission?.module || '—'}
          </div>
        </div>
      ),
    },
    {
      field: 'reviewerUserId', header: 'Reviewer', hideable: false, style: { minWidth: 180 },
      body: (r) => (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-ink">
            {nameOf(r.reviewerUserId)}
          </span>
          {r.isPrimary && <Tag value="Primary" severity="warning" />}
          {isWatcher(r) && <Tag value="Watcher" severity="secondary" />}
        </div>
      ),
    },
    {
      field: 'reviewStatus', header: 'State', align: 'center', style: { width: 120 },
      body: (r) => <Tag value={r.reviewStatus}
        severity={r.reviewStatus === 'REVIEWED' ? 'success' : 'secondary'} />,
    },
    {
      field: 'reviewOutcome', header: 'Outcome', style: { minWidth: 180 },
      body: (r) => (r.reviewOutcome
        ? <Tag value={r.reviewOutcome} severity="info" />
        : <span className="text-xs text-gray-400">not decided</span>),
    },
    {
      // Unconditional, as on the submissions grid: the list is merged across
      // projects by default, so the row is ambiguous without it.
      field: 'appId', header: 'Project', style: { minWidth: 190 },
      body: (r) => <ProjectCell appId={r.appId} projects={projects} />,
    },
    {
      field: 'assignedAt', header: 'Waiting since', sortable: true, style: { width: 170 },
      body: (r) => (
        <span className="text-xs text-ink-muted">
          {r.assignedAt ? new Date(r.assignedAt).toLocaleString() : '—'}
        </span>
      ),
    },
    {
      field: 'actions', header: 'Actions', hideable: false, align: 'center', style: { width: 170 },
      body: (r) => (
        <div className="flex items-center justify-center gap-1">
          <Button aria-label="View" icon="pi pi-eye" text rounded tooltip="See the feedback"
            onClick={() => { setOpenRow(r); setOpenSubmissionId(r.feedbackSubmissionId); }} />
          {/* A watcher is kept informed, never asked to decide. The server
              refuses their verdict too — this only avoids offering it. */}
          {!isWatcher(r) && canReview && (
            <Button aria-label={r.reviewStatus === 'REVIEWED' ? 'Change my verdict' : 'Record my verdict'} icon="pi pi-check-square" text rounded
              tooltip={r.reviewStatus === 'REVIEWED' ? 'Change my verdict' : 'Record my verdict'}
              onClick={() => openDialog(r)} />
          )}
        </div>
      ),
    },
  ], [nameOf, canReview, projects]);

  /* Held in a variable, not inlined: the queue and the opened feedback both
     render it, so a verdict can be recorded from either without a second copy
     of the form drifting from this one. */
  const verdictDialog = (
    <>
      {/* ------------------------------------------------- the verdict dialog */}
      <Dialog
        visible={!!target}
        onHide={closeDialog}
        header="Record your verdict"
        style={{ width: 620, maxWidth: '95vw' }}
        modal
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Cancel" severity="secondary" outlined onClick={closeDialog}
              disabled={saveM.isPending} />
            <Button label="Save" icon="pi pi-check"
              onClick={() => { setSubmitted(true); if (!errors.length && target) saveM.mutate(target); }}
              disabled={saveM.isPending} />
          </div>
        }
        data-testid="FeedbackReviews.Dialog.Verdict"
      >
        <div className="flex flex-col gap-3">
          {/* The dialog names its subject too — the last chance to notice the
              verdict is about to land on something other than what you meant. */}
          <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
            <div className="text-xs text-ink-muted">Your verdict is about</div>
            <div className="font-mono text-sm font-semibold text-ink">
              {target?.submission?.feedbackKey || '—'}
            </div>
            <div className="text-xs text-ink-muted">
              {target?.submission?.component?.componentType || '—'}
              {target?.submission?.submittedAt
                ? ` · sent ${new Date(target.submission.submittedAt).toLocaleString()}`
                : ''}
            </div>
          </div>

          <FormField label="What did you conclude?" required info={OUTCOME_INFO}
            error={submitted && !outcome ? 'Pick an outcome.' : undefined}>
            <Dropdown value={outcome} options={outcomeOptions}
              optionLabel="label" optionValue="value" className="w-full"
              placeholder="Select an outcome"
              onChange={(e: { value: string }) => setOutcome(e.value)} />
          </FormField>

          <FormField label="Comments">
            <TextArea rows={3} value={comments} className="w-full text-sm"
              placeholder="Why you reached that conclusion."
              onChange={(v: string) => setComments(v)} />
          </FormField>

          <FormField label="Does something need fixing?">
            <div className="flex h-[38px] items-center gap-3">
              <InputSwitch checked={actionRequired} onChange={(e) => setActionRequired(!!e.value)} />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {actionRequired ? 'Yes' : 'No'}
              </span>
            </div>
          </FormField>

          {actionRequired && (
            <FormField label="What needs doing?" required
              error={submitted && !actionNotes.trim() ? 'Say what needs doing.' : undefined}>
              <TextArea rows={2} value={actionNotes} className="w-full text-sm"
                placeholder="Fix the intent routing rule for password queries."
                onChange={(v: string) => setActionNotes(v)} />
            </FormField>
          )}

          {target?.isPrimary ? (
            <Message severity="info" className="w-full justify-start"
              text="You are the primary reviewer — your verdict decides this case." />
          ) : (
            <Message severity="info" className="w-full justify-start"
              text="Your verdict is recorded and kept, but the primary reviewer's decides the outcome." />
          )}

          {submitted && errors.length > 0 && (
            <Message severity="error" className="w-full justify-start"
              text={`Cannot save yet — ${errors.join(' · ')}`} />
          )}
        </div>
      </Dialog>
    </>
  );

  // The feedback behind a review takes over the tab rather than stacking a second
  // dialog on the queue — it is a page's worth of material, not a peek.
  if (openSubmissionId) {
    return (
      <>
        {toast}
        <SubmissionDetail
          submissionId={openSubmissionId}
          onBack={() => { setOpenSubmissionId(null); setOpenRow(null); }}
          onChanged={invalidate}
          triage={false}
          actions={openRow && canReview && !isWatcher(openRow) ? (
            <Button
              label={openRow.reviewStatus === 'REVIEWED' ? 'Change my verdict' : 'Record my verdict'}
              icon="pi pi-check-square" size="small" outlined
              onClick={() => openDialog(openRow)}
              data-testid="FeedbackReviews.Detail.Verdict" />
          ) : null}
        />
        {verdictDialog}
      </>
    );
  }

  return (
    <>
      {toast}

      {!outcomesSeeded && (
        <Message severity="warn" className="mb-3 w-full justify-start"
          text="Review outcomes are not seeded (feedbackReviewOutcome). Restart the API or seed them — a verdict cannot be saved until then." />
      )}

      <SaasDataTable
        tableId="feedback-reviews"
        columns={columns}
        data={listQ.data?.items ?? []}
        loading={listQ.isFetching}
        dataKey="_id"
        emptyMessage={mine ? 'Nothing waiting for you.' : 'No reviews yet.'}
        totalRecords={listQ.data?.total ?? 0}
        first={(page - 1) * pageSize}
        rows={pageSize}
        onPage={(e: { first: number; rows: number }) => {
          setPageSize(e.rows);
          setPage(Math.floor(e.first / e.rows) + 1);
        }}
        onSort={() => undefined}
        onFilter={() => undefined}
        testId="FeedbackReviews"
        toolbarEnd={<ListCount total={listQ.data?.total} onRefresh={() => listQ.refetch()}
          loading={listQ.isFetching} testId="FeedbackReviews.Refresh" />}
        toolbarStart={<>
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
            <InputSwitch checked={mine} onChange={(e) => { setMine(!!e.value); setPage(1); }} />
            {mine ? 'My queue — waiting on me' : 'All reviews'}
          </label>
          {/* Narrows both views: my queue and all reviews. */}
          <ProjectFilter value={appFilter} onChange={(appId) => { setAppFilter(appId); setPage(1); }}
            testId="FeedbackReviews.ProjectFilter" minWidth={170} />
        </>}
      />

      {verdictDialog}

    </>
  );
};

export default ReviewsTab;
